/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { css, jsx } from '@compiled/react';

import {
	BrandHeading,
	type GroupData,
	type IconCardData,
	IconCardGroup,
} from '@af/design-system-docs-ui';
import { Stack, Text } from '@atlaskit/primitives';

import {
	guidanceCardComponents,
	guidanceCardFoundations,
	guidanceCardGetStarted,
	guidanceCardTokens,
} from '../../components/guidance-cards/guidance-card-content';
import { GuidanceCard } from '../../components/guidance-cards/guidance-cards';
import OverviewPage from '../../templates/overview-page';

// oxlint-disable-next-line import/extensions
import Content from './_assets/content.mdx';
import heroDark from './_assets/hero.dark.png';
import heroLight from './_assets/hero.light.png';

const gridOverrideStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	div: {
		padding: '0',
	},
});

const guidanceCards = [
	guidanceCardGetStarted,
	guidanceCardFoundations,
	guidanceCardComponents,
	guidanceCardTokens,
];

const contactUsDevCommunity = {
	href: 'https://community.developer.atlassian.com/',
	title: 'Visit developer community (public only)',
	description: 'Search the Atlassian Developer Community forum and post questions.',
};

const contactUsSlack = {
	href: 'https://go.atlassian.com/help-design-system',
	title: 'Slack a question (Atlassians only)',
	description: 'Reach out to our team in our Slack channel.',
};

const contactUsDesignCrit = {
	href: 'https://go.atlassian.com/ads-office-hours',
	title: 'Book a design critique (Atlassians only)',
	description: 'Request a crit session to get feedback on your designs.',
};

export const contactUsCards: GroupData<IconCardData>[] = [
	{
		title: 'Ask a question or book a critique',
		cards: [contactUsDevCommunity, contactUsSlack, contactUsDesignCrit],
	},
];

export const contactUsCardsInternal: GroupData<IconCardData>[] = [
	{
		title: 'Ask a question or book a critique',
		cards: [contactUsSlack, contactUsDesignCrit],
	},
];

export const contactUsCardsInternalDev: GroupData<IconCardData>[] = [
	{
		title: 'Ask a question',
		cards: [contactUsSlack],
	},
];

export const contactUsCardsExternal: GroupData<IconCardData>[] = [
	{
		title: 'Ask a question',
		cards: [contactUsDevCommunity],
	},
];

export default (): JSX.Element => {
	return (
		<OverviewPage
			categorySlug="contact-us"
			title="Contact us"
			description="How to get help from the Atlassian Design System team and contribute to the system."
			entries={[]}
			image={{
				light: heroLight,
				dark: heroDark,
			}}
		>
			<Stack space="space.800">
				<section>
					<Stack space="space.200">
						<BrandHeading as="h2" size="large">
							Check our guidance first
						</BrandHeading>
						<Text>Before you reach out, look for an answer in these places.</Text>
						<Stack space="space.100">
							{guidanceCards.map((card) => (
								<GuidanceCard card={card} />
							))}
						</Stack>
					</Stack>
				</section>
				<section>
					<Stack space="space.200">
						<BrandHeading as="h2" size="large">
							Ask a question or book a critique
						</BrandHeading>
						<IconCardGroup data={contactUsCards} />
						<Text as="p">
							Empowering you to ship value to your customers is a priority. To avoid being a
							bottleneck, we'd love to be involved early in your app implementation through our
							participation channels.
						</Text>
						<Text as="p">
							If you need to add something or believe something is missing, post a question in slack
							so that we can offer support and connections.
						</Text>
					</Stack>
				</section>
				<section css={gridOverrideStyles}>
					<Content />
				</section>
			</Stack>
		</OverviewPage>
	);
};
